import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { MemberNameLink, memberIdForNameColumn } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, isAdminOrOwner, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { api } from '@/lib/api';

import { downloadCustomReportCsv } from './download-custom-report-csv';
import { downloadCustomReportPdfExport } from './download-custom-report-pdf';
import { customReportExportFilename } from './custom-report-export-filename';
import { getCustomReportSource } from './custom-report-sources';
import { formatCustomReportCell } from './format-custom-report-cell';
import { ReportExportMenu } from './report-export-menu';
import { ReportFavoriteButton } from './report-favorite-button';
import { ReportRunSkeleton } from './report-run-skeleton';
import { ReportsSubnav } from './reports-subnav';

interface CustomReportDefinition {
  id: string;
  name: string;
  description: string | null;
  objectSource: string;
  columns: string[];
  createdBy: string | null;
}

interface CustomReportRunResult {
  objectSource: string;
  columns: string[];
  rows: Record<string, unknown>[];
  page: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export const CustomReportDetailPage = () => {
  const { id = '' } = useParams();
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const membership = useCurrentMembership();
  const userId = useAuthStore((state) => state.user?.id);
  const church = useChurchStore((state) => state.church);
  const churchId = church?.id;
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);
  const [page, setPage] = useState(1);

  const report = useQuery({
    queryKey: ['reports', 'custom', id],
    queryFn: () => api.get<CustomReportDefinition>(`/reports/custom/${id}`),
    enabled: Boolean(id),
  });

  const run = useQuery({
    queryKey: ['reports', 'custom', id, 'run', page],
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    queryFn: () =>
      api.get<CustomReportRunResult>(`/reports/custom/${id}/run`, {
        query: { page, pageSize: 25 },
      }),
  });

  const source = getCustomReportSource(report.data?.objectSource ?? '');
  const canEdit =
    hasPermission(membership, PERMISSIONS.REPORTS_CREATE) &&
    (isAdminOrOwner(membership) || report.data?.createdBy === userId);
  const canExport = hasPermission(membership, PERMISSIONS.REPORTS_EXPORT);

  const exportReport = useMutation({
    mutationFn: async (format: 'csv' | 'pdf') => {
      if (!churchId || !report.data) return;
      if (format === 'csv') {
        await downloadCustomReportCsv({
          churchId,
          reportId: id,
          fallbackFilename: customReportExportFilename(report.data.name, 'csv'),
        });
        return;
      }
      await downloadCustomReportPdfExport({
        reportId: id,
        reportTitle: report.data.name,
        reportDescription: report.data.description,
        objectSource: report.data.objectSource,
        churchName: church?.name ?? t('title'),
        branchName: church?.campusName,
        logoUrl: church?.logoUrl,
        locale,
        timezone,
        t,
        tCommon,
      });
    },
    onSuccess: (_data, format) => {
      toast.success(format === 'csv' ? t('run.exportDownloaded') : t('run.exportPdfDownloaded'));
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={report.data?.name ?? t('custom.run')}
        description={report.data?.description ?? undefined}
        headerActions={
          <div className="flex justify-end gap-2">
            {id ? (
              <ReportFavoriteButton
                favoriteRef={{ kind: 'custom', id }}
                churchId={churchId}
                userId={userId}
              />
            ) : null}
            {canEdit ? (
              <Button variant="secondary" onClick={() => navigate(`/reports/custom/${id}/edit`)}>
                <Pencil className="size-4" aria-hidden />
                {tCommon('action.edit')}
              </Button>
            ) : null}
            {canExport ? (
              <ReportExportMenu
                disabled={!report.data}
                loading={exportReport.isPending}
                format={exportReport.variables}
                onExport={(format) => exportReport.mutate(format)}
              />
            ) : null}
            <Button onClick={() => void run.refetch()} loading={run.isFetching}>
              <Play className="size-4" aria-hidden />
              {t('custom.run')}
            </Button>
          </div>
        }
      />
      <ReportsSubnav />

      <p className="text-body-sm text-fg-muted">
        <Link to="/reports/custom" className="inline-flex items-center gap-1 text-brand hover:underline">
          <ArrowLeft className="size-4" aria-hidden />
          {t('custom.backToList')}
        </Link>
      </p>

      {report.data ? (
        <p className="text-caption text-fg-tertiary">
          {t(`custom.sources.${report.data.objectSource}`, report.data.objectSource)}
        </p>
      ) : null}

      {run.isFetching ? (
        <ReportRunSkeleton columnCount={run.data?.columns.length || 3} />
      ) : run.data ? (
        <>
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="border-b border-border text-left text-caption text-fg-secondary">
                    {run.data.columns.map((column) => (
                      <th key={column} className="px-4 py-3 font-medium">
                        {t(`custom.fields.${column}`, column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {run.data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={run.data.columns.length} className="px-4 py-8 text-center text-fg-muted">
                        {tCommon('table.noResults')}
                      </td>
                    </tr>
                  ) : (
                    run.data.rows.map((row, index) => (
                      <tr key={String(row.id ?? index)} className="border-b border-border last:border-0">
                        {run.data!.columns.map((column) => {
                          const field = source?.fields.find((item) => item.key === column);
                          const memberId = memberIdForNameColumn(column, row);
                          const value = row[column];
                          return (
                            <td key={column} className="px-4 py-3 align-top">
                              {memberId && typeof value === 'string' && value.trim() ? (
                                <MemberNameLink memberId={memberId} name={value} />
                              ) : (
                                formatCustomReportCell(field, value, row, t, locale)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <Pagination
            page={run.data.page.page}
            pageSize={run.data.page.pageSize}
            totalItems={run.data.page.totalItems}
            totalPages={run.data.page.totalPages}
            onPageChange={setPage}
            labels={{
              showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </>
      ) : null}
    </div>
  );
};
